import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IconChevronLeft } from "@tabler/icons-react";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import { getCountriesList } from "../../domains/app/utils/countries";
import {
  reloadCurrentUser,
  replaceCurrentUserData,
  getCurrentUserData,
} from "../../domains/user/hooks/useCurrentUser";
import { apiClient } from "../../services/api";
import { useMeasuredHeight } from "../../hooks/use-measured-height";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { CountryStep } from "./steps/CountryStep";
import { StepSearchInput } from "./components/StepSearchInput";
import { useFixupReturnTo } from "./useFixupReturnTo";

// Standalone country picker for SSO users who reach the app without a country
// (AppRoutes redirects them here). Reuses the onboarding CountryStep for the
// selection itself and owns only the submit + redirect.
export default function CountryFixupPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const returnToRequestedPage = useFixupReturnTo();
  const [country, setCountry] = useState("");
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const headerOverlay = useMeasuredHeight();
  const footerOverlay = useMeasuredHeight();
  // The search field sits in the overlaid footer; the page shrinks above the
  // keyboard instead of letting iOS pan the whole web view.
  useKeyboardManaged();

  const isValid = country in getCountriesList();

  async function handleSubmit() {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { country },
      });
      // Update the cache synchronously so the AppRoutes gate sees the new
      // country on the next render — otherwise the user briefly bounces back
      // to this page while the background refetch is still in flight.
      const cached = getCurrentUserData();
      if (cached !== null) {
        replaceCurrentUserData({ ...cached, country });
      }
      returnToRequestedPage();
      void reloadCurrentUser();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      setIsSubmitting(false);
    }
  }

  if (isDesktopLayout()) {
    return (
      <DesktopCountryFixup
        country={country}
        search={search}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCountryChange={setCountry}
        onSearchChange={setSearch}
        onSubmit={() => {
          void handleSubmit();
        }}
        onBack={() => {
          history.goBack();
        }}
      />
    );
  }

  return (
    <KeyboardAvoidingView
      behavior="height"
      className="absolute inset-0 bg-[#fbfbfb]"
    >
      {/* Scrolling list, padded to clear (and scroll behind) the overlaid bands. */}
      <div
        className="absolute inset-0 overflow-y-auto px-5"
        style={{
          paddingTop: headerOverlay.height,
          paddingBottom: footerOverlay.height,
        }}
      >
        <CountryStep
          country={country}
          searchQuery={search}
          onCountryChange={setCountry}
        />
      </div>

      {/* Header overlay (back + title) */}
      <div
        ref={headerOverlay.ref}
        className="absolute inset-x-0 top-0 z-20 bg-[#fbfbfb]/70 backdrop-blur-md"
      >
        <div
          className="flex items-center px-6 pb-2"
          style={{ paddingTop: "calc(1rem + var(--safe-area-top))" }}
        >
          <button
            type="button"
            aria-label={t("Back")}
            className="-ml-1 flex size-6 cursor-pointer items-center justify-center bg-transparent"
            onClick={() => {
              history.goBack();
            }}
          >
            <IconChevronLeft className="size-6 text-primary-black" />
          </button>
        </div>
        <div className="px-5 pt-1 pb-5">
          <h1 className="font-dm-sans text-heading-display font-semibold text-[#2c2c2c]">
            {t("Where do you practice?")}
          </h1>
        </div>
      </div>

      {/* Footer overlay (search + CTA) */}
      <div
        ref={footerOverlay.ref}
        className="absolute inset-x-0 bottom-0 z-20 bg-[#fbfbfb]/70 backdrop-blur-md"
      >
        <div
          className="flex flex-col gap-3 px-5 pt-4"
          style={{ paddingBottom: "max(var(--safe-area-bottom), 2rem)" }}
        >
          <StepSearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search countries")}
            variant="mobile"
          />
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={!isValid || isSubmitting}
            onClick={handleSubmit}
          >
            {t("Continue")}
          </Button>
        </div>
      </div>
    </KeyboardAvoidingView>
  );
}

// Desktop layout: the same country picker in a bounded centered card (static
// header/footer, scrolling list between them) instead of the full-screen
// phone layout with overlaid bands.
function DesktopCountryFixup({
  country,
  search,
  isValid,
  isSubmitting,
  onCountryChange,
  onSearchChange,
  onSubmit,
  onBack,
}: {
  country: string;
  search: string;
  isValid: boolean;
  isSubmitting: boolean;
  onCountryChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-ds-slate-50 p-8">
      <div className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-2xl border border-ds-black/8 bg-white shadow-sm">
        {/* Header (back + title) */}
        <div className="flex items-center px-4 pt-4">
          <button
            type="button"
            aria-label={t("Back")}
            className="flex size-6 cursor-pointer items-center justify-center bg-transparent"
            onClick={onBack}
          >
            <IconChevronLeft className="size-6 text-primary-black" />
          </button>
        </div>
        <div className="px-6 pt-2 pb-4">
          <h1 className="font-dm-sans text-heading-display font-semibold text-[#2c2c2c]">
            {t("Where do you practice?")}
          </h1>
        </div>

        {/* Scrolling list */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
          <CountryStep
            country={country}
            searchQuery={search}
            onCountryChange={onCountryChange}
          />
        </div>

        {/* Footer (search + CTA) */}
        <div className="flex flex-col gap-3 border-t border-ds-black/8 px-6 py-4">
          <StepSearchInput
            value={search}
            onChange={onSearchChange}
            placeholder={t("Search countries")}
            variant="mobile"
          />
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={!isValid || isSubmitting}
            onClick={onSubmit}
          >
            {t("Continue")}
          </Button>
        </div>
      </div>
    </div>
  );
}
